import { lazy, Suspense, useCallback, useEffect, useState, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation, useParams, useSearchParams } from "react-router-dom";
import { getBackgroundLocation } from "@/lib/settingsModal";
import { useIsMobile } from "@/hooks/use-mobile";
import type { Session } from "@supabase/supabase-js";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { shouldForceLogout } from "@/lib/sessionValidation";
import { lastAdminPath } from "@/lib/adminReturn";
import { AccountDeletedScreen } from "@/components/auth/AccountDeletedScreen.tsx";
import AgentGate from "@/components/auth/AgentGate.tsx";
import { StempelAktywnosci } from "@/hooks/useStempelAktywnosci";
import { isClaimToken, stashClaimToken } from "@/lib/claimFlow";
import { stashSeekerToken } from "@/lib/seekerFlow";
import { stashInviteToken } from "@/lib/inviteFlow";
import { isProduction, isProductionGated, hasAccessInvite, pokazujPaneleDev } from "@/config/env";
import { rootRouteFor } from "@/lib/homeRoute";
import AuthPage from "./components/auth/AuthPage.tsx";
import { ScrollToTop } from "./components/layout/ScrollToTop.tsx";
import { DocumentTitle } from "./components/layout/DocumentTitle.tsx";
import { SupportMessengerProvider } from "./components/layout/SupportMessengerProvider.tsx";
import { SupportGuestButton } from "./components/layout/SupportGuestButton.tsx";
import RouteErrorBoundary from "./components/layout/RouteErrorBoundary.tsx";
import SamonaprawaWdrozenia from "./components/layout/SamonaprawaWdrozenia.tsx";
import PhoneVerifiedGuard from "./components/onboarding/PhoneVerifiedGuard.tsx";
import AdminGate from "./components/admin/AdminGate.tsx";
import ChromeFrame from "./components/admin/ChromeFrame.tsx";
import ComingSoon from "./pages/ComingSoon.tsx";
import NotFound from "./pages/NotFound.tsx";
import TrasaBezSesji from "./components/auth/TrasaBezSesji";
import { TERMS_ROUTE, PRIVACY_POLICY_ROUTE } from "@/lib/legalRoutes";

/**
 * STRONY SPOZA PIERWSZEGO WEJŚCIA — LENIWIE (30.09). Były importowane wprost i jechały w startowym
 * JS-ie każdej strony (profil, ustawienia, powiadomienia, przejęcie ogłoszenia, strony prawne…).
 * Świadomie ZOSTAJĄ statycznie: logowanie (AuthPage), bramka „wkrótce” (ComingSoon), 404 i
 * PhoneVerifiedGuard. Onboarding od 30.09 też leniwie — niżej (sam import, routing bez zmian).
 * Wszystkie te trasy są w <Suspense> niżej; nakładka ustawień ma własny.
 */
const WaitlistPage = lazy(() => import("./pages/WaitlistPage.tsx"));
const ProfilePage = lazy(() => import("./pages/ProfilePage.tsx"));
const SettingsPage = lazy(() => import("./pages/SettingsPage.tsx"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage.tsx"));
const GuarantorDashboard = lazy(() => import("./pages/GuarantorDashboard.tsx"));
const ClaimLandingPage = lazy(() => import("./pages/ClaimLandingPage.tsx"));
const ClaimSuccessPage = lazy(() => import("./pages/ClaimSuccessPage.tsx"));
const ClaimReviewPage = lazy(() => import("./pages/ClaimReviewPage.tsx"));
const LegalPlaceholderPage = lazy(() => import("./pages/LegalPlaceholderPage.tsx"));
const AccountDeletedPage = lazy(() => import("./pages/AccountDeletedPage.tsx"));
const SuburbsPreviewPage = lazy(() => import("./pages/SuburbsPreviewPage.tsx"));
const PricePanelPreviewPage = lazy(() => import("./pages/PricePanelPreviewPage.tsx"));
const OwnerWizardPreviewPage = lazy(() => import("./pages/OwnerWizardPreviewPage.tsx"));
const VerificationLadderPreviewPage = lazy(() => import("./pages/VerificationLadderPreviewPage"));
const ClaimDemandPreviewPage = lazy(() => import("./pages/ClaimDemandPreviewPage"));
const HomePage = lazy(() => import("./pages/HomePage.tsx"));
const SearchPage = lazy(() => import("./pages/SearchPage.tsx"));
/**
 * ONBOARDING LENIWIE (decyzja Piotra 30.09, porządki #10). Te strony i `BasicInfoStep` były
 * importowane statycznie, więc KAŻDA strona — także /search dla gościa — ściągała w starcie
 * `calendar` (27 KB gzip), `PhotoUploader` i resztę kroków onboardingu. Zmierzone na buildzie
 * produkcyjnym (/search, Slow 4G + CPU ×4, telefon, mediana z 3): start 434 → 346 KB gzip,
 * koniec startu JS 4027 → 3173 ms, LCP 6988 → 6456 ms. Samo przeniesienie importu — żadnej
 * zmiany zachowania; routing (Konwencja 9, OnboardingRouter) bez zmian. Zastępnik trzyma
 * wysokość strony, więc przy dociąganiu kroku nic nie skacze.
 */
const BasicInfoStep = lazy(() => import("./components/onboarding/BasicInfoStep.tsx"));
const OnboardingRouter = lazy(() => import("./pages/LoginSuccessPage.tsx"));
const OnboardingEssentialsPage = lazy(() => import("./pages/OnboardingEssentialsPage.tsx"));
const OnboardingGoLivePage = lazy(() => import("./pages/OnboardingGoLivePage.tsx"));
const OnboardingMiastoPage = lazy(() => import("./pages/OnboardingMiastoPage.tsx"));
const OnboardingProfileCreationPage = lazy(() => import("./pages/OnboardingProfileCreationPage.tsx"));

/** Zastępnik na czas dociągania leniwego kroku onboardingu — wysokość okna pod nagłówkiem. */
const ZastepnikStrony = () => (
  <div aria-busy="true" className="min-h-[calc(min(100svh,var(--app-ih,9999px))-var(--app-header-h,57px))]" />
);
const Leniwie = ({ children }: { children: ReactNode }) => <Suspense fallback={<ZastepnikStrony />}>{children}</Suspense>;

const StyleguidePage = lazy(() => import("./pages/StyleguidePage.tsx"));
const OfferPage = lazy(() => import("./pages/OfferPage.tsx"));
const FavoritesPage = lazy(() => import("./pages/FavoritesPage.tsx"));
// Toasty Radix (use-toast) — wołają je tylko okna wiadomości i zgłoszeń, a biblioteka
// (@radix-ui/react-toast) jechała w startowym JS-ie każdej strony. Leniwie od 30.09 (−4 KB gzip).
// Toast wywołany ZANIM ten kawałek dojdzie nie ginie: stan żyje w module use-toast
// (`memoryState`), a `useToast` startuje od niego przy montowaniu — test toaster.leniwy.test.tsx.
// Sonner (większość powiadomień) zostaje statyczny.
const Toaster = lazy(() => import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })));
// Powłoka /admin z panelem bocznym (sidebar.tsx, ~13 KB min) — dotąd w startowym JS-ie KAŻDEJ
// strony, choć dotyczy wyłącznie operatorów za AdminGate. Leniwie od 30.09: start −7 KB gzip.
const AdminLayout = lazy(() => import("./components/admin/AdminLayout.tsx"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage.tsx"));
const AiCostsPage = lazy(() => import("./pages/AiCostsPage.tsx"));
const AdminScrapingPage = lazy(() => import("./pages/AdminScrapingPage.tsx"));
const AdminIngestRunPage = lazy(() => import("./pages/AdminIngestRunPage.tsx"));
const AdminAgenciesPage = lazy(() => import("./pages/AdminAgenciesPage.tsx"));
const AdminCityLeadsPage = lazy(() => import("./pages/AdminCityLeadsPage.tsx"));
const SpieciePage = lazy(() => import("./pages/SpieciePage.tsx"));
const PipelineInboxPage = lazy(() => import("./pages/PipelineInboxPage.tsx"));
const AdminLeadsPage = lazy(() => import("./pages/AdminLeadsPage.tsx"));
const AdminUsersPage = lazy(() => import("./pages/AdminUsersPage.tsx"));
const AdminUserDetailPage = lazy(() => import("./pages/AdminUserDetailPage.tsx"));
const AdminListingsPage = lazy(() => import("./pages/AdminListingsPage.tsx"));
const AdminListingDetailPage = lazy(() => import("./pages/AdminListingDetailPage.tsx"));
const AdminSettingsPage = lazy(() => import("./pages/AdminSettingsPage.tsx"));
const AdminReportsPage = lazy(() => import("./pages/AdminReportsPage.tsx"));
const AdminProfileVisibilityPage = lazy(() => import("./pages/AdminProfileVisibilityPage.tsx"));
const AdminConversationsPage = lazy(() => import("./pages/AdminConversationsPage.tsx"));
const AdminResearchPage = lazy(() => import("./pages/AdminResearchPage.tsx"));
const SeekerLandingPage = lazy(() => import("./pages/SeekerLandingPage.tsx"));
const OwnerLandingPage = lazy(() => import("./pages/OwnerLandingPage.tsx"));
const AlertyMieszkanPage = lazy(() => import("./pages/AlertyMieszkanPage.tsx"));
const DoWynikowZAlertu = lazy(() => import("./pages/AlertyMieszkanPage.tsx").then((m) => ({ default: m.DoWynikowZAlertu })));
const OwnerWizardPage = lazy(() => import("./pages/OwnerWizardPage.tsx"));
const OwnerPublishedPage = lazy(() => import("./pages/OwnerPublishedPage.tsx"));
const MyApartmentPage = lazy(() => import("./pages/MyApartmentPage.tsx"));
const OwnerSeekersPage = lazy(() => import("./pages/OwnerSeekersPage.tsx"));
const OwnerCalendarPage = lazy(() => import("./pages/OwnerCalendarPage.tsx"));
const ListingEditHubPage = lazy(() => import("./pages/ListingEditHubPage.tsx"));
const PriceLabPage = lazy(() => import("./pages/PriceLabPage.tsx"));
const PetsEditPage = lazy(() => import("./pages/PetsEditPage.tsx"));
const DescriptionEditPage = lazy(() => import("./pages/DescriptionEditPage.tsx"));
const FreshnessEditPage = lazy(() => import("./pages/FreshnessEditPage.tsx"));
const CommunityProfilePage = lazy(() => import("./pages/CommunityProfilePage.tsx"));
const CommunityGridPage = lazy(() => import("./pages/CommunityGridPage.tsx"));
const MessagesPage = lazy(() => import("./pages/MessagesPage.tsx"));

/** Legacy /u/:userId share links now live under /community/:userId. */
const LegacyProfileRedirect = () => {
  const { userId } = useParams<{ userId: string }>();
  return <Navigate to={`/community/${userId}`} replace />;
};

/** /admin lands on the operator's last-used console (fallback: inbox). */
const AdminIndexRedirect = () => <Navigate to={lastAdminPath() ?? '/admin/inbox'} replace />;

/**
 * Settings-as-overlay, MOBILE ONLY (see src/lib/settingsModal.ts): when the
 * current location carries `state.backgroundLocation`, the page tree keeps
 * rendering that background location — the page the user was on stays mounted
 * (scroll, filters, everything) while the settings overlay sits on top.
 * Desktop ignores the state entirely: /settings is an ordinary page there
 * (left rail + content column), so the tree just renders it.
 */
const RoutesWithBackground = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const background = isMobile ? getBackgroundLocation(location) : undefined;
  return <Routes location={background ?? location}>{children}</Routes>;
};

/** Renders the mobile settings overlay above the background page tree. Only
 *  mounts for in-app modal navigations; direct /settings loads (and all of
 *  desktop) are handled by the /settings route inside the main tree. */
const SettingsModalOutlet = () => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const background = getBackgroundLocation(location);
  if (!isMobile || !background || location.pathname !== '/settings') return null;
  // Poza głównym <Suspense> — SettingsPage jest leniwe, więc własna granica.
  return (
    <Suspense fallback={null}>
      <SettingsPage />
    </Suspense>
  );
};

// Sane caching defaults. Without these React Query uses staleTime:0 +
// refetchOnWindowFocus:true, so every tab-focus / navigation refetched the SERP
// list + pins + count (measured: ~10.7k SERP calls = ~43% of DB time — a
// refetch-storm that drove the Disk IO budget toward depletion). 5-min staleness
// is fine: realtime subscriptions push the updates that must be instant
// (notifications, the open offer row), and hooks that truly need a fresh full
// fetch keep their own override (e.g. useSourcePost's initialDataUpdatedAt:0).
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/**
 * Stash an ?invite=<token> from the URL into sessionStorage so it survives the
 * auth → onboarding redirect chain (which drops query params). BasicInfoStep
 * reads it back to pre-set the role and link the invite.
 */
const InviteCapture = () => {
  const [params] = useSearchParams();
  useEffect(() => {
    const token = params.get("invite");
    // Use localStorage (not sessionStorage) so the token survives the OTP
    // redirect chain, iOS WebView ↔ Safari context switches, and tab reloads —
    // but with a lifetime (see flowToken.ts), so it can't reshape a signup
    // weeks later.
    if (token) stashInviteToken(token);
  }, [params]);
  return null;
};

/**
 * Pre-public access counterpart: stash ?access=<token> from a temporary-access
 * invite link so AuthPage can resolve it to the invited email and pre-fill the
 * login form. The access grant itself is keyed by email server-side (the
 * flat.access_gate auth hook) — this token only drives the email prefill.
 */
const AccessCapture = () => {
  const [params] = useSearchParams();
  useEffect(() => {
    const token = params.get("access");
    if (token) localStorage.setItem("fh_access_token", token);
  }, [params]);
  return null;
};

/**
 * Landlord-claim counterpart to InviteCapture: stash ?claim=<token> so it
 * survives the auth → onboarding redirect chain. BasicInfoStep / OnboardingRouter
 * read it back via src/lib/claimFlow.ts.
 */
const ClaimCapture = () => {
  const [params] = useSearchParams();
  useEffect(() => {
    const token = params.get("claim");
    if (token) stashClaimToken(token);
  }, [params]);
  return null;
};

/**
 * Post-auth hop that KEEPS a link-borne ?claim= in the address.
 *
 * There are two very different signals that a signup is a listing transfer:
 * the token arrived with THIS navigation (a fact about this arrival), or it is
 * merely sitting in localStorage (a guess about this device — the one that
 * hijacked an unrelated signup on 2026-07-30). BasicInfoStep now grades them
 * differently, so the difference has to survive the redirect — and a plain
 * <Navigate> silently drops the query string, which is why the strong signal
 * never reached the form and everyone was asked to declare they are an owner.
 */
const NavigateKeepingClaim = ({ to }: { to: string }) => {
  const [params] = useSearchParams();
  const claim = params.get("claim");
  return <Navigate to={isClaimToken(claim) ? `${to}?claim=${claim}` : to} replace />;
};

/**
 * Seeker counterpart: stash ?seeker=<token> so it survives the auth → onboarding
 * redirect chain. OnboardingRouter reads it back via src/lib/seekerFlow.ts.
 */
const SeekerCapture = () => {
  const [params] = useSearchParams();
  useEffect(() => {
    const token = params.get("seeker");
    if (token) stashSeekerToken(token);
  }, [params]);
  return null;
};

const App = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accountDeleted, setAccountDeleted] = useState(false);

  // Authoritative, server-side session check. The local JWT stays valid ~1h after the account is
  // deleted (auth.users row gone), so getSession() alone can't tell a live account from a deleted
  // one — getUser() hits the server. Force sign-out ONLY on a definitive invalid-session signal;
  // never on a transient network error (see src/lib/sessionValidation.ts). Runs off the render
  // path (no startup friction) and re-runs on tab focus so deletion is caught within seconds.
  const validateSession = useCallback(async () => {
    const { data: { session: current } } = await supabase.auth.getSession();
    if (!current) return; // nothing to validate when logged out
    let res;
    try {
      res = await supabase.auth.getUser();
    } catch {
      return; // thrown network error → transient, keep the session
    }
    if (shouldForceLogout(res)) {
      await supabase.auth.signOut();
      setAccountDeleted(true);
    }
  }, []);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setIsLoading(false);
    });

    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession);
      setIsLoading(false);
      if (currentSession) void validateSession();
    });

    return () => subscription.unsubscribe();
  }, [validateSession]);

  // Re-validate when the user returns to the tab, so a deletion during an active session is caught
  // immediately rather than only when the access token expires (~1h later).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void validateSession();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [validateSession]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  // A just-deleted account: shown outside the router, survives the sign-out. Sending the user to a
  // full reload gives a clean logged-out entry point (and clears any stale cached state).
  if (accountDeleted) {
    return <AccountDeletedScreen onDismiss={() => window.location.assign('/')} />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Suspense fallback={null}>
          <Toaster />
        </Suspense>
        <Sonner />
        {/* A logged-in visit = activity for the alert e-mails' 15-day rule (C8, AlertEmails §4). */}
        <StempelAktywnosci uid={session?.user?.id} />
        <BrowserRouter>
          <ScrollToTop />
          <DocumentTitle />
          <InviteCapture />
          <AccessCapture />
          <ClaimCapture />
          <SeekerCapture />
          <SupportMessengerProvider>
          <SupportGuestButton />
          <ChromeFrame>
              <SamonaprawaWdrozenia />
              <RouteErrorBoundary>
              <Suspense fallback={null}>
              <RoutesWithBackground>
              {/* "/" is login-only again (Maciek 23.10, rootRouteFor). The new homepage is
                  previewed at /home on the developer sites only; elsewhere /home goes to "/". */}
              <Route path="/" element={(() => {
                const r = rootRouteFor(!!session, isProductionGated(), hasAccessInvite());
                if (r === 'coming_soon') return <ComingSoon />;
                return <Navigate to={r === 'onboarding_router' ? '/login-success' : `/${r}`} replace />;
              })()} />
              <Route path="/home" element={
                pokazujPaneleDev()
                  ? <AgentGate session={session}><HomePage /></AgentGate>
                  : <Navigate to="/" replace />
              } />
              {/* Public browse surfaces. Gated for AGENTS only (AgentGate is a
                  pass-through for logged-out visitors, tenants and owners):
                  a signed-in agent must not reach tenant profiles or the SERP. */}
              <Route element={<AgentGate session={session}><Outlet /></AgentGate>}>
                <Route path="/community" element={<CommunityGridPage />} />
                <Route path="/community/:userId" element={<CommunityProfilePage />} />
                {/* Legacy share path → canonical community url */}
                <Route path="/u/:userId" element={<LegacyProfileRedirect />} />
                <Route path="/search" element={<SearchPage />} />
                {/* Landing hops of the alert e-mail links (C8): /search with Moje filtry (+ the panel). */}
                <Route path="/alerty-mieszkan/wyniki" element={<DoWynikowZAlertu zalogowany={!!session} />} />
                <Route path="/alerty-mieszkan/filtry" element={<DoWynikowZAlertu panel zalogowany={!!session} />} />
                <Route path="/offer/:id" element={<OfferPage />} />
                {/* Ulubione oferty (SRCH-13). W tej samej grupie co wyszukiwarka, wiec
                    obowiazuje ta sama bramka: agent tu nie wejdzie, a niezalogowany tak —
                    jego lista siedzi w przegladarce i ma ja gdzie zobaczyc. */}
                <Route path="/ulubione" element={<FavoritesPage />} />
              </Route>
              {/* Legal placeholders — public (linked from the signup terms
                  checkbox, opened in a new tab). Real documents pending; see
                  docs/PreLaunchChecklist.md. */}
              <Route path={TERMS_ROUTE} element={<LegalPlaceholderPage document="terms" />} />
              <Route path={PRIVACY_POLICY_ROUTE} element={<LegalPlaceholderPage document="privacy" />} />
              {/* Post-deletion confirmation (plan P3.4) — PUBLIC and outside AgentGate: the
                  session is dead the moment this renders, so any guard would bounce to login. */}
              <Route path="/konto-usuniete" element={<AccountDeletedPage />} />
              {/* Public landlord-claim landing (unauthenticated visitor; token = secret) */}
              <Route path="/claim/:token" element={<ClaimLandingPage />} />
              {/* Public demand-side seeker landing + demand-first owner landing (token = secret) */}
              <Route path="/dla-ciebie/:token" element={<SeekerLandingPage />} />
              <Route path="/wlasciciel/:token" element={<OwnerLandingPage />} />
              {/* „Wyłącz alerty” from an alert e-mail (C8, AlertEmails §3.5) — PUBLIC, token = secret */}
              <Route path="/alerty-mieszkan" element={<AlertyMieszkanPage />} />
              {/* Backoffice — one AdminGate over the whole /admin shell (AdminLayout) */}
              <Route element={<AdminGate><AdminLayout /></AdminGate>}>
                {/* /admin reopens the operator's last-used console (fallback: inbox). */}
                <Route path="/admin" element={<AdminIndexRedirect />} />
                <Route path="/admin/inbox" element={<PipelineInboxPage />} />
                <Route path="/admin/spiecie" element={<SpieciePage />} />
                <Route path="/admin/leads" element={<AdminLeadsPage />} />
                <Route path="/admin/scraping" element={<AdminScrapingPage />} />
                <Route path="/admin/scraping/bieg/:runId" element={<AdminIngestRunPage />} />
                <Route path="/admin/users" element={<AdminUsersPage />} />
                <Route path="/admin/users/:id" element={<AdminUserDetailPage />} />
                <Route path="/admin/profile-visibility" element={<AdminProfileVisibilityPage />} />
                <Route path="/admin/listings" element={<AdminListingsPage />} />
                <Route path="/admin/listings/:id" element={<AdminListingDetailPage />} />
                <Route path="/admin/analytics" element={<AnalyticsPage />} />
                <Route path="/admin/ai-costs" element={<AiCostsPage />} />
                <Route path="/admin/agencies" element={<AdminAgenciesPage />} />
                <Route path="/admin/city-leads" element={<AdminCityLeadsPage />} />
                {/* Trafność dopasowań jest teraz zakładką analityki — stary adres zostaje
                    jako przekierowanie, bo krąży po notatkach i zakładkach przeglądarki. */}
                <Route
                  path="/admin/match-stats"
                  element={<Navigate to="/admin/analytics?tab=portale" replace />}
                />
                <Route path="/admin/reports" element={<AdminReportsPage />} />
                <Route path="/admin/conversations" element={<AdminConversationsPage />} />
                <Route path="/admin/research" element={<AdminResearchPage />} />
                <Route path="/admin/settings" element={<AdminSettingsPage />} />
              </Route>
              {/* Legacy path → new home under the shell */}
              <Route path="/analytics" element={<Navigate to="/admin/analytics" replace />} />
              {import.meta.env.DEV && (
                <>
                  <Route path="/suburbs" element={<SuburbsPreviewPage />} />
                  <Route path="/price-panel-preview" element={<PricePanelPreviewPage />} />
                  <Route path="/owner-wizard-preview/:stepId" element={<OwnerWizardPreviewPage />} />
                  <Route path="/verification-ladder-preview" element={<VerificationLadderPreviewPage />} />
                  <Route path="/claim-demand-preview" element={<ClaimDemandPreviewPage />} />
                </>
              )}
              {/* Living styleguide (Convention 19): available in dev AND on
                  non-production deploys (maciej/piotr/bartek previews, stage)
                  so non-CLI teammates can browse the token catalogue. Excluded
                  from the public release build. */}
              {(import.meta.env.DEV || !isProduction()) && (
                <Route path="/styleguide" element={<StyleguidePage />} />
              )}
              {session ? (
                <>
                  <Route path="/hubabuba" element={<NavigateKeepingClaim to="/login-success" />} />
                  <Route path="/login" element={<NavigateKeepingClaim to="/login-success" />} />
                  <Route path="/signup" element={<NavigateKeepingClaim to="/login-success" />} />
                  <Route path="/login-success" element={<Leniwie><OnboardingRouter /></Leniwie>} />
                  <Route path="/onboarding/basic-info" element={<Leniwie><BasicInfoStep /></Leniwie>} />
                  {/* Retired route (Onboarding Revamp slice 2): the SMS code is
                      basic-info Stage B now. Old bookmarks/links redirect. */}
                  <Route path="/onboarding/sms-verify" element={<Navigate to="/onboarding/basic-info" replace />} />
                  {/* Settings stays reachable for EVERY role incl. agents — it
                      owns the email/phone change and account deletion. */}
                  <Route path="/settings" element={<SettingsPage />} />
                  {/* Everything a v1 agent must not touch. Tenants, owners and
                      guarantors pass straight through (see AgentGate).
                      /onboarding/basic-info is deliberately OUTSIDE the gate —
                      agents still have to verify their phone. */}
                  <Route element={<AgentGate session={session}><Outlet /></AgentGate>}>
                    <Route path="/onboarding/waitlist" element={<WaitlistPage />} />
                    {/* Legacy route — the suburbs screen became the merged Miasto step */}
                    <Route path="/onboarding/suburbs" element={<Navigate to="/onboarding/miasto" replace />} />
                    <Route path="/onboarding/miasto" element={<Leniwie><OnboardingMiastoPage /></Leniwie>} />
                    <Route path="/onboarding/essentials" element={<Leniwie><OnboardingEssentialsPage /></Leniwie>} />
                    {/* Retired route (Onboarding Revamp slice 4): the AI step is
                        replaced by essentials. Old bookmarks/links redirect. */}
                    <Route path="/onboarding/ai-description" element={<Navigate to="/onboarding/essentials" replace />} />
                    <Route path="/onboarding/profile" element={<Leniwie><OnboardingProfileCreationPage /></Leniwie>} />
                    <Route path="/onboarding/go-live" element={<Leniwie><OnboardingGoLivePage /></Leniwie>} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/notifications" element={<NotificationsPage />} />
                    <Route path="/messages" element={<MessagesPage />} />
                    <Route path="/guarantor" element={<GuarantorDashboard />} />
                    <Route path="/claim/success" element={<ClaimSuccessPage />} />
                    <Route path="/owner/wizard/:stepId" element={<OwnerWizardPage />} />
                    <Route path="/owner/published" element={<OwnerPublishedPage />} />
                    <Route path="/owner/listing" element={<MyApartmentPage />} />
                    <Route path="/owner/listing/seekers" element={<OwnerSeekersPage />} />
                    <Route path="/owner/calendar" element={<OwnerCalendarPage />} />
                    <Route path="/owner/claim/review" element={<ClaimReviewPage />} />
                    <Route path="/owner/listing/edit" element={<ListingEditHubPage />} />
                    <Route path="/owner/price-lab" element={<PriceLabPage />} />{/* interactive price-work mockup */}
                    <Route path="/owner/listing/edit/pets" element={<PetsEditPage />} />
                    <Route path="/owner/listing/edit/description" element={<DescriptionEditPage />} />
                    <Route path="/owner/listing/edit/freshness" element={<FreshnessEditPage />} />
                  </Route>
                  <Route
                    path="/coming-soon"
                    element={
                      <PhoneVerifiedGuard>
                        <ComingSoon />
                      </PhoneVerifiedGuard>
                    }
                  />
                </>
              ) : (
                <>
                  <Route path="/login" element={<AuthPage mode="login" />} />
                  <Route path="/signup" element={<AuthPage mode="signup" />} />
                  <Route path="/hubabuba" element={<AuthPage mode="login" />} />
                  {/* Placeholder target for not-yet-built nav/footer links shown
                      to logged-out visitors (the authed branch has its own guarded one). */}
                  <Route path="/coming-soon" element={<ComingSoon />} />
                </>
              )}
              {/* Logged out on an auth-only route (/profile, /owner/…) → /login, then back
                  (OnboardingRouter redeems it). Anything else is a real 404. */}
              <Route path="*" element={session ? <NotFound /> : <TrasaBezSesji />} />
              </RoutesWithBackground>
              {session && <SettingsModalOutlet />}
              </Suspense>
              </RouteErrorBoundary>
          </ChromeFrame>
          </SupportMessengerProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
